import I18n from "i18n-js";
import React, { Fragment } from "react";
import { connect } from "react-redux";
import { bool, func, instanceOf, oneOfType, shape, string } from "prop-types";
import { ROUTE_INSTALLATIONS } from "../../../routes";
import FormPage from "../../common/components/layout/FormPage";
import SubmitButton from "../../common/components/form/SubmitButton";
import DownloadButton from "../../common/components/elements/DownloadButton";
import { importCompressors } from "../actions/installationsActions";
import { FILE_ENCODINGS, readFileContent } from "../../common/utils/fileUtils";
import ImportCompressorsContent from "./ImportCompressors/ImportCompressorsContent";
import InstallationUtils from "../utils/InstallationUtils";
import { getNomenclatures } from "../../nomenclatures/actions/nomenclaturesActions";
import { translateNomenclature } from "../../common/utils/langUtils";
import { sleep } from "../../common/utils/timeUtils";

class ImportCompressors extends FormPage {
  static propTypes = {
    auth: shape(),
    error: oneOfType([shape(), bool]),
    success: bool,
    tsvReport: shape({
      content: instanceOf(Blob).isRequired,
      name: string.isRequired,
    }),
    nomenclaturesList: shape(),
    getNomenclatures: func.isRequired,
    importCompressors: func.isRequired,
  };

  constructor() {
    super();

    this.state = {
      file: null,
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onSelectedFile = this.onSelectedFile.bind(this);
    this.hasSelectedFile = this.hasSelectedFile.bind(this);
    this.onDownloadTemplate = this.onDownloadTemplate.bind(this);
  }

  componentDidMount() {
    this.props.getNomenclatures();
  }

  /**
   * {@inheritdoc}
   */
  componentDidUpdate(prevProps) {
    const { error, success } = this.props;

    if (prevProps.error === error || prevProps.success === success) {
      return;
    }

    if (error || success) {
      this.setState({ loading: false });
    }
  }

  onSelectedFile(e) {
    const file = e.target.files.length > 0 && e.target.files[0];
    this.setState({ file });
  }

  onSubmit() {
    const { importCompressors } = this.props;
    const { file } = this.state;

    this.setState({ loading: true });

    readFileContent(file, FILE_ENCODINGS.TSV).then((tsvContent) => {
      importCompressors(InstallationUtils.processImportCompressorsFileContent(tsvContent));
    });
  }

  hasSelectedFile() {
    return !!this.state.file;
  }

  /**
   * {@inheritdoc}
   */
  getTitle() {
    return I18n.t("pages.installations.import_compressors.title");
  }

  /**
   * {@inheritdoc}
   */
  getBackRoute() {
    return ROUTE_INSTALLATIONS;
  }

  getNomenclaturesForImport() {
    const { lang } = this.props.auth;
    const { componentNatureTypes, componentNatureClassifications, componentNatures } = this.props.nomenclaturesList;
  
    // Get nomenclatures specific to compressors
    const compressorNature = componentNatures.find((nature) => nature.compressor);
    const brands = compressorNature?.brands ?? [];
    const types = componentNatureTypes.filter(t => t.natureUuid === compressorNature?.uuid);
    const classifications = componentNatureClassifications.filter(c => c.natureUuid === compressorNature?.uuid);

    const generateNames = (items, translate = false) => {
      return items.map((item) => {
        const designation = translate ? translateNomenclature(item, "designation", lang) : item.designation;
        return `${designation} (${item.uuid})`;
      });
    };

    const typesNames = generateNames(types, true);
    const classificationsNames = generateNames(classifications, true);
    const brandsNames = generateNames(brands);
  
    return {
      typesNames,
      classificationsNames,
      brandsNames
    };
  }

  async onDownloadTemplate(retry = 0) {
    if (!this.props.nomenclaturesList) {
      await sleep(500);

      // Retry 3 times waiting for the nomenclatures to be loaded
      if (retry < 3) {
        this.onDownloadTemplate(retry + 1);
      }

      return;
    }

    const { typesNames, classificationsNames, brandsNames } = this.getNomenclaturesForImport();

    InstallationUtils.downloadCompressorsImportTemplate(typesNames, classificationsNames, brandsNames);
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { loading } = this.state;
    const { error, success, tsvReport } = this.props;

    return (
      <Fragment>
        <ImportCompressorsContent
          downloadSample={
            <a href="#" onClick={this.onDownloadTemplate}>
              {I18n.t("pages.installations.import_compressors.file_sample")}
            </a>
          }
        />

        <span style={{ marginRight: "10px" }}>
          {I18n.t("pages.installations.import_compressors.file_input")}
        </span>
        <input type="file" accept=".csv, .tsv, .ods" onChange={this.onSelectedFile} />
        <SubmitButton
          loading={loading}
          onClick={this.onSubmit}
          label={I18n.t("pages.installations.import_compressors.submit")}
          disabled={!this.hasSelectedFile()}
        />
        <br />
        {success && (
          <div className="text-primary">
            <span>{I18n.t("pages.installations.import_compressors.success")}</span>
          </div>
        )}
        {error && (
          <div className="text-danger">
            <span>{I18n.t("pages.installations.import_compressors.error")}</span>
          </div>
        )}
        {error && typeof error === "object" && (
          <div>
            {error.detail.split("\n").map((message) => (
              <span className="text-danger" key={message}>
                {message}
              </span>
            ))}
          </div>
        )}
        {tsvReport && (
          <div>
            <p className="text-warning">
              ⚠︎ {I18n.t("pages.installations.import_compressors.tsv_report.explanation")}
            </p>
            <DownloadButton
              fileContent={() => tsvReport.content}
              filename={tsvReport.name}
              label={I18n.t(
                "pages.installations.import_compressors.tsv_report.download_btn",
              )}
            />
          </div>
        )}
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  error: state.importCompressors.error,
  success: state.importCompressors.success,
  tsvReport: state.importCompressors.tsvReport,
  nomenclaturesList: state.nomenclatures.nomenclatures,
});

const mapDispatchToProps = (dispatch) => ({
  getNomenclatures: () => dispatch(getNomenclatures()),
  importCompressors: (tsvContent) => dispatch(importCompressors(tsvContent)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ImportCompressors);